import React, { useContext, useCallback } from 'react'
import PropTypes from 'prop-types'

import { Button } from '../../atoms/button'
import { AuthContext } from '../../../context/authProvider'
import useSlugs from '../../../hooks/useSlugs'
import { sendRegistrationEvent } from '../../../utils/commonDataLayer'
import { sendMobileAppEvent } from '../../../utils/mobileAppFunctionality'
import { useModalContext } from '../../../context/modalProvider'
import { ModalTypes } from '../../../utils/constants'
import { If } from '../../atoms/if'
import { FieldCpfSubmit } from '../fieldCpfSubmit'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { navigate } from 'gatsby'

const LoggedInOutButton = (props) => {
  const {
    addCpfField,
    loggedIn,
    loggedOut,
    forceLogin,
    eventId,
    onBlur,
    onFocus,
    onClick,
  } = props
  const [loginSlug, registerSlug] = useSlugs(['login', 'register'])
  const { isLoggedIn } = useContext(AuthContext)
  const CTAsInUse = isLoggedIn ? loggedIn : loggedOut
  const { open } = useModalContext()
  const REGISTRATION_PAGE_FLOW_ENABLED  = useFeatureIsOn('fe_registration_page_flow')
  
  if (
    !CTAsInUse ||
    CTAsInUse.length == 0 ||
    (!CTAsInUse[0]?.link && !CTAsInUse[0]?.action_text)
  )
    return <></>

  const onRegister = () => {
    if (!sendMobileAppEvent('navRegister')) {
      sendRegistrationEvent(`cta-${eventId}`)

      if(REGISTRATION_PAGE_FLOW_ENABLED){
         navigate(`/${registerSlug}`)
         return
      }

      open({ type: ModalTypes.REGISTER })
    }
  }

  const registerWithCPF = (cpf) => {
    if (!sendMobileAppEvent('navRegister')) {
      sendRegistrationEvent(`cta-${eventId}`)

    if (REGISTRATION_PAGE_FLOW_ENABLED) {
      navigate(`/${registerSlug}/?nationalId=${cpf}`)
      return
    }

    open({ type: ModalTypes.REGISTER, data: { idToRegister: cpf } })
    }
  }

  const createButtonsProps = useCallback(
    (cta) => {
      const buttonProps = {}

      if (cta?.link?.startsWith(`/${registerSlug}`)) {
        buttonProps.onClick = onRegister
        return buttonProps
      }

      if (forceLogin && !isLoggedIn) {
        buttonProps.href = `/${loginSlug}/`
        buttonProps.redirectURL = cta.link
        return buttonProps
      }

      buttonProps.href = cta.link
      return buttonProps
    },
    [forceLogin, isLoggedIn]
  )

  const renderButton = (cta, i) => {
    const buttonProps = createButtonsProps(cta)

    const handleClick = () => {
      if (buttonProps?.onClick) buttonProps?.onClick()
      if (onClick) onClick()
    }

    return (
      <Button
        key={`loggedInOut-button-${i}`}
        style={{
          backgroundColor: cta.background_color,
          color: cta.text_color,
        }}
        {...buttonProps}
        onClick={handleClick}
      >
        {cta.action_text}
      </Button>
    )
  }

  return CTAsInUse?.map((cta, i) => {
    return (
      <If
        key={`loggedInOut-button-${i}`}
        condition={isLoggedIn || !addCpfField}
        render={() => renderButton(cta, i)}
        renderElse={() => (
          <FieldCpfSubmit
            key={`cpf-input-button-${i}`}
            customCTAButton={cta}
            buttonBackgroundColor={cta.background_color}
            buttonTextColor={cta.text_color}
            onSubmit={registerWithCPF}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={onClick}
          />
        )}
      />
    )
  })
}

LoggedInOutButton.defaultProps = {
  addCpfField: false,
  forceLogin: false,
  loggedIn: [],
  loggedOut: [],
  onBlur: () => {},
  onFocus: () => {},
  onClick: () => {},
}

LoggedInOutButton.propTypes = {
  addCpfField: PropTypes.bool,
  forceLogin: PropTypes.bool,
  eventId: PropTypes.string,
  loggedIn: PropTypes.arrayOf(
    PropTypes.shape({
      action_text: PropTypes.string,
      link: PropTypes.string,
      background_color: PropTypes.string,
      text_color: PropTypes.string,
    })
  ),
  loggedOut: PropTypes.arrayOf(
    PropTypes.shape({
      action_text: PropTypes.string,
      link: PropTypes.string,
      background_color: PropTypes.string,
      text_color: PropTypes.string,
    })
  ),
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onClick: PropTypes.func,
}

export { LoggedInOutButton }
